import React, { useState, useEffect, useRef, useContext } from 'react';

import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import ButtonPrimaryEllipse from '../ButtonPrimaryEllipse';
import SlideShowNav from '../SlideShowNav/SlideShowNav';
import PhotoCred from '../PhotoCred';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import { GradientContext } from '../../contexts/PageContexts';
import { trackReadMore } from '../../utils/dataLayer';
import { SwiperSlide } from 'swiper/react';
import dynamic from 'next/dynamic';

const SlideShow = dynamic(() => import('../SlideShow'), { ssr: false });

import styles from './RelatedContent.module.scss';
import useWindowWidthTitles from '../../hooks/useWindowWidthTitles';

const RelatedContent = ({
    titleTop,
    titleBottom,
    items = [],
    backgroundColor,
    isArticlesListPage = false,
    isDesktop,
    showLinkText = false,
    modifiers = [],
}) => {
    const [index, setIndex] = useState(0);
    const [isHovered, setIsHovered] = useState([]);

    const itemRefs = useRef([]);

    const gradient = useContext(GradientContext);

    const [ref, isVisible] = useIntersectionObserver(0, undefined, true);
    const { t } = useTranslation();

    const width = useWindowWidthTitles(isDesktop);

    const isMobile = width < 768;

    const rootClasses = classNames(
        styles['Root'],
        styles[gradient],
        {
            [styles['Visible']]: isVisible,
            [styles['White']]: backgroundColor !== 'black',
            [styles['Black']]: backgroundColor === 'black',
            [styles['ArticlesListPage']]: isArticlesListPage,
        },
        modifiers.map((m) => styles[m])
    );

    const titleClasses = classNames(styles['Title'], 'grid', {
        [styles['Visible']]: isVisible,
    });

    const titleMobileClasses = classNames(styles['TitleMobile'], {
        [styles['Visible']]: isVisible,
    });

    const itemContainerClasses = classNames(styles['ItemContainer'], 'grid', {
        [styles['ThreeItems']]: items.length === 3,
        [styles['EnoughItems']]: items.length <= 3,
        [styles['NotEnoughItems']]: items.length > 3,
    });

    const sliderContainer = classNames(styles['SliderContainer'], {
        [styles['EnoughItems']]: items.length > 3,
        [styles['NotEnoughItems']]: items.length <= 3,
    });

    const renderNewsItems = () =>
        items.map((item, i) => {
            const variants = {
                initialImage: {
                    opacity: 0,
                    y: -40,
                },
                animateImage: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: 1,
                        delay: i * 0.3 + 0.8,
                    },
                },
                initialText: {
                    opacity: 0,
                    y: 40,
                },
                animateText: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: 1,
                        delay: i * 0.3 + 0.8,
                    },
                },
                hover: {
                    y: -10,
                    transition: {
                        type: 'spring',
                        stiffness: 50,
                        duration: 0.3,
                    },
                },
                initial: {
                    y: 0,
                    transition: {
                        type: 'spring',
                        stiffness: 50,
                        duration: 0.3,
                    },
                },
            };

            const itemClasses = classNames(styles['Item'], {
                [styles['FirstItem']]: i === 0,
                [styles['LastItem']]: i === items.length - 1,
                [styles['Single']]: items.length === 1,
                [styles['Double']]: items.length === 2,
                [styles['Triple']]: items.length === 3,
                [styles['Active']]: index === i,
            });

            const isExternal =
                item?.href?.href?.indexOf('http://') !== -1 || item?.href?.href?.indexOf('https://') !== -1;

            const handleTrackReadMore = (e) => {
                e.preventDefault();

                trackReadMore(item?.title, 'Related content');

                setTimeout(() => {
                    window.location.href = item?.href?.href;
                }, 200);
            };

            return (
                <SwiperSlide
                    className={itemClasses}
                    key={i}
                    ref={(el) => (itemRefs.current[i] = el)}
                    onMouseEnter={() => setIsHovered([true, i])}
                    onMouseLeave={() => setIsHovered([false, i])}>
                    <motion.div
                        className={styles['ImageContainer']}
                        variants={variants}
                        initial="initialImage"
                        animate={[
                            isVisible ? 'animateImage' : 'initialImage',
                            isHovered[0] && isHovered[1] === i ? 'hover' : '',
                            !isHovered[0] && isHovered[1] === i ? 'initial' : '',
                        ]}>
                        <Image
                            className={styles['Image']}
                            src={item?.image?.url}
                            alt={item?.image?.alt}
                            title={item?.image?.title}
                            fill
                            draggable={false}
                        />
                        <PhotoCred credit={item?.image?.credit} />
                    </motion.div>
                    <motion.div
                        className={`${styles['Content']} black-glass`}
                        variants={variants}
                        initial="initialText"
                        animate={isVisible ? 'animateText' : 'initialText'}>
                        <h3 className={styles['ItemTitle']}>{item?.title}</h3>
                        <div className={styles['ItemText']}>{item?.text}</div>
                        <div className={styles['ButtonContainer']}>
                            {!!item?.href?.href && (
                                <div className={styles['Button']}>
                                    <ButtonPrimaryEllipse
                                        asLink
                                        href={item?.href?.href}
                                        onClick={handleTrackReadMore}
                                        icon={isExternal ? 'icon-external-white' : 'icon-arrow-right-white'}
                                        iconDimensions={isExternal ? [18, 18] : [20, 20]}>
                                        {showLinkText ? item?.href?.text : t('Generic.readMore')}
                                        <span className="sr-only">{item?.title}</span>
                                    </ButtonPrimaryEllipse>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </SwiperSlide>
            );
        });

    return (
        <section className={rootClasses} ref={ref}>
            {titleTop &&
                (isMobile ? (
                    <h2 className={titleMobileClasses}>
                        <span className={titleClasses}>
                            <span className={styles['TitleContainer']}>
                                <span className={styles['TitleTop']}>{titleTop}</span>
                                <span className={styles['TitleBottom']}>{titleBottom}</span>
                            </span>
                        </span>
                    </h2>
                ) : (
                    <h2 className={styles['TitleDesktop']}>
                        <span className={titleClasses}>
                            <span className={styles['TitleTop']}>{titleTop}</span>
                            <span className={styles['TitleBottom']}>{titleBottom}</span>
                        </span>
                    </h2>
                ))}

            {isArticlesListPage && (
                <div className={classNames(styles['ArticlesListWrapper'], 'grid')}>
                    <div className={styles['ArticlesListGrid']}>
                        <div className={styles['ArticlesListItems']}>{renderNewsItems()}</div>
                    </div>
                </div>
            )}

            {!isArticlesListPage && (
                <>
                    <ul className={itemContainerClasses}>{renderNewsItems()}</ul>
                    <div className={sliderContainer}>
                        <SlideShow
                            slidesPerView={'auto'}
                            spaceBetween={16}
                            spaceBetweenDesktop={24}
                            onSlideChange={(e) => {
                                setIndex(e.activeIndex);
                            }}>
                            {renderNewsItems()}
                            {items.length > 1 && (
                                <SlideShowNav
                                    indicators={items}
                                    indicatorIndex={index}
                                    setIndicatorIndex={setIndex}
                                    isVisible={isVisible}
                                    color={'black'}
                                />
                            )}
                        </SlideShow>
                    </div>
                </>
            )}
        </section>
    );
};

RelatedContent.propTypes = {};

export default RelatedContent;
